import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { FileUp } from 'lucide-react';
import { Track } from '../../../components/ui';

const BATCH_SIZE = 10;
const MAX_LONG_SIDE = 2000;
const ACCEPTED_EXT = ['.jpg', '.jpeg', '.png', '.pdf'];
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'application/pdf'];
const HEIC_MESSAGE = "HEIC photos aren't supported. Convert to JPG or PNG first.";
const UNSUPPORTED_MESSAGE = 'Only JPG, PNG and PDF files are accepted.';

function isHeic(file: File): boolean {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif') || type.includes('heic') || type.includes('heif');
}

function isAccepted(file: File): boolean {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return ACCEPTED_EXT.some((ext) => name.endsWith(ext)) || ACCEPTED_MIME.includes(type);
}

/**
 * Client-side downscale of an image file to at most `MAX_LONG_SIDE` px on its
 * long side. PDFs pass through untouched (the backend rasterises those
 * itself). Anything that goes wrong — unsupported browser API, a file that
 * isn't decodable — falls back to uploading the original file rather than
 * blocking the upload.
 */
async function downscale(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const longSide = Math.max(bitmap.width, bitmap.height);
    if (longSide <= MAX_LONG_SIDE) {
      bitmap.close?.();
      return file;
    }
    const scale = MAX_LONG_SIDE / longSide;
    const targetW = Math.max(1, Math.round(bitmap.width * scale));
    const targetH = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, file.type || 'image/jpeg', 0.92));
    if (!blob) return file;
    return new File([blob], file.name, { type: blob.type || file.type, lastModified: Date.now() });
  } catch {
    return file;
  }
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

interface DropZoneProps {
  /** Uploads one batch (≤10 files); called once per batch, awaited in order. */
  onUpload: (files: File[]) => Promise<unknown>;
  disabled?: boolean;
}

export default function DropZone({ onUpload, disabled = false }: DropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploading = progress != null;

  async function handleFiles(fileList: FileList | File[]) {
    const all = Array.from(fileList);
    if (all.length === 0) return;

    const accepted: File[] = [];
    let heicCount = 0;
    let unsupportedCount = 0;
    for (const f of all) {
      if (isHeic(f)) { heicCount += 1; continue; }
      if (!isAccepted(f)) { unsupportedCount += 1; continue; }
      accepted.push(f);
    }

    if (heicCount > 0 || unsupportedCount > 0) {
      const parts: string[] = [];
      if (heicCount > 0) parts.push(`${heicCount} HEIC file${heicCount === 1 ? '' : 's'} skipped: ${HEIC_MESSAGE}`);
      if (unsupportedCount > 0) parts.push(`${unsupportedCount} file${unsupportedCount === 1 ? '' : 's'} skipped: ${UNSUPPORTED_MESSAGE}`);
      setRejection(parts.join(' '));
    } else {
      setRejection(null);
    }

    if (accepted.length === 0) return;

    const downscaled = await Promise.all(accepted.map(downscale));
    const batches = chunk(downscaled, BATCH_SIZE);
    let done = 0;
    setProgress({ done, total: downscaled.length });
    try {
      for (const batch of batches) {
        await onUpload(batch);
        done += batch.length;
        setProgress({ done, total: downscaled.length });
      }
    } finally {
      setProgress(null);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    void handleFiles(e.dataTransfer.files);
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) void handleFiles(files);
    e.target.value = '';
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div>
      <div
        id="drop"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || uploading}
        onClick={() => !(disabled || uploading) && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !(disabled || uploading)) inputRef.current?.click();
        }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled && !uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[18px] border-2 border-dashed px-5 py-[30px] text-center text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          dragOver ? 'border-accent bg-accent-bg' : 'border-line-strong bg-surface-2'
        } ${disabled || uploading ? 'cursor-not-allowed opacity-60' : 'hover:border-accent hover:bg-accent-bg'}`}>
        <span className="grid h-11 w-11 place-items-center rounded-[14px] bg-mist text-slate">
          <FileUp size={22} strokeWidth={1.8} aria-hidden />
        </span>
        <p className="font-semibold">
          Drag photos or a PDF of the answer sheets here, or click to choose files
        </p>
        <p className="text-[13px] text-muted">JPG, PNG or PDF. Uploads in batches of {BATCH_SIZE}. Keep all four corner marks in the photo.</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={[...ACCEPTED_MIME, ...ACCEPTED_EXT].join(',')}
          className="hidden"
          disabled={disabled || uploading}
          onChange={onInputChange}
          aria-label="Choose sheet photos or PDF"
        />
      </div>

      {rejection && (
        <p role="alert" className="mt-2 text-xs font-semibold text-warning-ink">{rejection}</p>
      )}

      {progress && (
        <div className="mt-3 space-y-1.5">
          <Track value={pct} label="Upload progress" />
          <p className="font-mono text-xs text-muted">{progress.done} of {progress.total} uploaded</p>
        </div>
      )}
    </div>
  );
}
